import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LogoutIcon from '@mui/icons-material/Logout';
import { getImpersonation, endImpersonation } from '../services/authStorage';

// Persistent top banner shown ONLY in a read-only "View as user" tab (a tab
// whose per-tab sessionStorage carries an impersonation token). The admin's own
// tab has no impersonation state, so this renders nothing there. Exiting clears
// the per-tab session and closes the tab (falling back to /login if the browser
// blocks window.close on a tab it didn't script-open).
const BANNER_HEIGHT = 40;

const ImpersonationBanner = () => {
  const [imp] = useState(() => getImpersonation());

  // Auto-clear if the 30-minute token has elapsed while the tab sat open.
  useEffect(() => {
    if (!imp?.expiresAt) return undefined;
    const ms = new Date(imp.expiresAt).getTime() - Date.now();
    if (ms <= 0) { handleExit(); return undefined; }
    const t = setTimeout(handleExit, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!imp) return null;

  const handleExit = () => {
    endImpersonation();
    try {
      window.close();
    } catch (_) { /* ignore */ }
    // window.close() is a no-op for tabs the script didn't open — redirect so
    // the tab doesn't sit on a now-token-less page.
    setTimeout(() => { window.location.href = '/login'; }, 50);
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: BANNER_HEIGHT,
          zIndex: (theme) => theme.zIndex.snackbar + 1,
          bgcolor: 'warning.dark',
          color: 'warning.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          boxShadow: 3,
        }}
      >
        <VisibilityIcon fontSize="small" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Impersonating {imp.name}
          {imp.email ? ` (${imp.email})` : ''}
        </Typography>
        <Chip label="Limited access" size="small" color="default" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(0,0,0,0.25)', color: 'inherit' }} />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant="contained"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={handleExit}
          sx={{ color: 'warning.dark', bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}
        >
          Exit
        </Button>
      </Box>
      {/* Spacer so the fixed banner doesn't cover page content. */}
      <Box sx={{ height: BANNER_HEIGHT }} />
    </>
  );
};

export default ImpersonationBanner;
